import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../hooks/useApp';
import useGameStore from '../stores/gameStore';
import { useAccount, useDisconnect } from 'wagmi';
import useWallet from '../hooks/useWallet';
import SnowAnimation from '../components/SnowAnimation';
import LeftSidebar from '../components/LeftSidebar';
import RightSidebar from '../components/RightSidebar';
import ChatButton from '../components/ChatButton';
import BombIcon from '../components/BombIcon';
import './GameLayout.css';
import { 
  ArrowLeftIcon, 
  Bars3Icon, 
  XMarkIcon,
  WalletIcon,
  StarIcon,
  CubeIcon
} from '@heroicons/react/24/outline';
import { useAppKit } from '@reown/appkit/react';
import clsxm from '../utils/clsxm';

const GameLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { username: _username, leaveRoom } = useApp();
  const { isConnected } = useAccount();
  const { open: _open } = useAppKit()
  const { connectWallet, formattedAddress, formattedBalance } = useWallet();
  const { disconnect: _disconnect } = useDisconnect();
  const { 
    playerScore, 
    arsenalItems, 
    selectedSlot, 
    showHuntAgain, 
    onHuntAgain, 
    onLeaveGame
  } = useGameStore();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatSidebarOpen, setChatSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if we're on the game page
  const isGamePage = location.pathname.includes('/game-lobby/game') || location.pathname.includes('/game-lobby/waiting');


  // console.log("🚀 ~ GameLayout ~ isGamePage:", isGamePage, location.pathname)

  // Check if mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleBackToDashboard = () => {
    navigate('/dashboard');
    setSidebarOpen(false);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const closeChatSidebar = () => {
    setChatSidebarOpen(false);
  };

  const openChatSidebar = () => {
    setChatSidebarOpen(true);
  };

  return (
    <div className="game-layout App  bg-[url('./assets/images/background1.jpg')] bg-fixed bg-cover bg-center">
      <SnowAnimation />
      
      {/* Landscape Mode Warning for Mobile */}
      {isMobile && (
        <div className="landscape-warning fixed inset-0 bg-black/90 z-50 items-center justify-center p-4 hidden landscape:flex">
          <div className="text-center text-white max-w-sm">
            <div className="mb-4">
              <svg className="w-16 h-16 mx-auto text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <h2 className="text-xl font-bold mb-2">Please Rotate Your Device</h2>
            <p className="text-gray-300">For the best gaming experience, please rotate your device to portrait mode.</p>
          </div>
        </div>
      )}
      
      {/* Mobile Left Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Left Sidebar Component */}
      <LeftSidebar 
        isOpen={sidebarOpen} 
        onClose={closeSidebar} 
        isGamePage={isGamePage} 
      />

      {/* Mobile Chat Sidebar Overlay */}
      {chatSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeChatSidebar}
        />
      )}

      {/* Right Sidebar Component */}
      <RightSidebar 
        isOpen={chatSidebarOpen} 
        onClose={closeChatSidebar} 
        isGamePage={isGamePage} 
      />

      {/* Chat Button */}
      <ChatButton 
        onClick={openChatSidebar} 
        isGamePage={isGamePage} 
      />

      {/* Desktop Navbar */}
      <nav className="desktop-navbar hidden lg:block bg-gray-900/95 backdrop-blur-lg border-b border-gray-700/50 shadow-lg sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* show logo if isGamePage is false*/}
            {!isGamePage && (
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg flex items-center justify-center shadow-lg">
                  <BombIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-xl font-bold text-emerald-400">Bomberman</div>
                  <div className="text-xs text-emerald-300">Hunt</div>
                </div>
              </div>
            )}
            {/* Left side - Score (only on game page) */}
            {isGamePage && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 flex items-center justify-center">
                    <StarIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-emerald-400">{playerScore}</div>
                    <div className="text-xs text-emerald-300">points</div>
                  </div>
                </div>
              </div>
            )}

            {/* Center - Arsenal (only on game page) */}
            {isGamePage && (
              <div className="arsenal-section flex items-center gap-3">
                <span className="text-sm font-medium text-gray-300 mr-2">Arsenal:</span>
                {[0, 1, 2, 3].map((slotIndex) => {
                  const item = arsenalItems[slotIndex];
                  const isSelected = selectedSlot === slotIndex;
                  
                  return (
                    <div 
                      key={slotIndex} 
                      className={clsxm('arsenal-slot w-12 h-12 rounded-lg border-2 p-1 transition-all duration-200', 
                        isSelected 
                          ? 'selected border-emerald-400 bg-emerald-500/20 shadow-lg shadow-emerald-500/30':
                          'border-gray-600 bg-gray-800/50 hover:border-gray-500'
                      )}
                    >
                      {item && item.type ? (
                        <img 
                          src={`/sprites/${item.type}s/${item.type}_${item.id || 'normal'}.png`} 
                          alt={item.type}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.src = `/sprites/${item.type}s/${item.type}_normal.png`;
                          }}
                        />
                      ) : slotIndex === 0 ? (
                        <img 
                          src="/sprites/bombs/bomb_normal.png" 
                          alt="bomb"
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-500">
                          <CubeIcon className="h-4 w-4" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Right side - Action Buttons */}
            <div className="flex items-center gap-3">
              {/* Wallet Connection */}
              {!isConnected ? (
                <button 
                  onClick={connectWallet}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center gap-2"
                >
                  <WalletIcon className="h-4 w-4" />
                  Connect Wallet
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <div className="text-sm font-medium text-emerald-400">{formattedAddress}</div>
                    <div className="text-xs text-gray-400">{formattedBalance}</div>
                  </div>
                  <button 
                  onClick={ connectWallet}
                    // onClick={disconnect}
                    className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white font-medium py-2 px-3 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center gap-2"
                  >
                    Disconnect
                  </button>
                </div>
              )}

              {/* Hunt Again Button (only on game page) */}
              {isGamePage && showHuntAgain && (
                <button 
                  className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center gap-2"
                  onClick={onHuntAgain}
                >
                  <span>Hunt Again!</span>
                </button>
              )}

              {/* Leave Game Button (only on game page) */}
              {isGamePage && (
                <button 
                  className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center gap-2"
                  onClick={() => {
                    // Call onLeaveGame if it exists, otherwise use leaveRoom directly
                    if (onLeaveGame && typeof onLeaveGame === 'function') {
                      onLeaveGame();
                    } else {
                      // Fallback: use leaveRoom from useApp which handles navigation
                      leaveRoom();
                    }
                  }}
                  title="Leave current game"
                >
                  <span>Leave Game</span>
                </button>
              )}

              {/* Back to Dashboard */}
              <button 
                onClick={handleBackToDashboard}
                className="bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center gap-2"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Header */}
      <div className="mobile-header lg:hidden bg-gray-900/95 backdrop-blur-lg border-b border-gray-700/50 shadow-lg sticky top-0 z-30">
        <div className="flex items-center justify-between h-16 px-4">
          {/* Mobile Menu Button */}
          <button
            onClick={toggleSidebar}
            className="text-white hover:text-gray-300 p-2"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Mobile Title */}
          <div className="header-title flex items-center gap-2">
            {isGamePage && (
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 flex items-center justify-center">
                <StarIcon className="h-4 w-4 text-white" />
              </div>
            )}
            <span className="text-white font-semibold">
              {isGamePage ? 'Game' : 'Lobby'}
            </span>
          </div>

          {/* Mobile Wallet Status */}
          <div className="wallet-status flex items-center gap-2">
            {isConnected ? (
              <div className="text-right">
                <div className="text-xs text-emerald-400">{formattedAddress?.slice(0, 6)}...</div>
                <div className="text-xs text-gray-400">{formattedBalance?.split(' ')[0]} BNB</div>
              </div>
            ) : (
              <button 
                onClick={connectWallet}
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs px-3 py-1 rounded-lg"
              >
                Connect
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Game Content */}
      <div className={`game-content flex overflow-auto ${isMobile ? 'pt-0' : 'pt-0'} items-center h-[calc(100vh-66px)] justify-center`}>
        <Outlet />
      </div>

      {/* Mobile Sidebars */}
      <LeftSidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        isGamePage={isGamePage}
      />
      
      <RightSidebar 
        isOpen={chatSidebarOpen} 
        onClose={() => setChatSidebarOpen(false)}
        isGamePage={isGamePage}
      />

      {/* Mobile Chat Button */}
      <ChatButton 
        onClick={() => setChatSidebarOpen(true)}
        isGamePage={isGamePage}
      />
    </div>
  );
};

export default GameLayout;
